import { VALID_PSP_VALUES } from "@/internals/constants/psp";

import type { ProviderCookieValue, ProviderIdentifier } from "./types";

/**
 * Pure core of the provider-cookie module: it owns the cookie's name, its
 * serialized representation and its merge semantics, and performs no I/O at all.
 * Every runtime adapter (client, server, in-memory) is a thin shell over these
 * functions, so the rules are defined and tested exactly once.
 */

export const PROVIDER_COOKIE_NAME = "provider";

/**
 * `JSON.stringify` drops keys whose value is `undefined`, which is how a
 * `update({ status: undefined })` clears a field. Relied upon by
 * `regenerateTransaction`, which resets the status while keeping the new orderId.
 */
export const serializeProviderCookie = (value: ProviderCookieValue): string =>
  JSON.stringify(value);

/**
 * Accepts whatever the runtime hands us: the server jar yields a raw JSON
 * string, while the client cookie library already parses JSON cookies into
 * objects. Anything unusable (absent, malformed, non-object) reads as
 * `undefined` rather than throwing — a corrupt cookie must degrade to "no
 * transaction in flight", not break the render of `/payment`.
 *
 * Partially-populated records are returned as-is: writers merge into the cookie
 * incrementally, so callers that need a complete record must ask
 * {@link isRenderableProviderCookie}.
 */
export const deserializeProviderCookie = (raw: unknown): ProviderCookieValue | undefined => {
  if (raw === undefined || raw === null || raw === "") return undefined;

  if (typeof raw === "object") {
    return Array.isArray(raw) ? undefined : (raw as ProviderCookieValue);
  }

  if (typeof raw !== "string") return undefined;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return undefined;
    return parsed as ProviderCookieValue;
  } catch {
    // Malformed cookie: fall through to "absent". Deliberately not logged — a
    // stale or truncated cookie is an expected, self-healing condition.
    return undefined;
  }
};

/**
 * Shallow overlay of `partial` onto `current` — the cookie shape is flat, and an
 * explicit `undefined` in the partial clears the key (see
 * {@link serializeProviderCookie}).
 */
export const mergeProviderCookie = (
  current: ProviderCookieValue | undefined,
  partial: Partial<ProviderCookieValue>
): ProviderCookieValue => ({ ...current, ...partial }) as ProviderCookieValue;

const PROVIDER_IDENTIFIERS: ReadonlySet<ProviderIdentifier> = new Set(VALID_PSP_VALUES);

/**
 * Whether a value is one of the payment providers we have an implementation for.
 * For validating untrusted input (a `psp` query param, the short-lived PSP
 * cookie) — deliberately *not* used to validate the provider cookie itself, see
 * {@link isRenderableProviderCookie}.
 */
export const isProviderIdentifier = (value: unknown): value is ProviderIdentifier =>
  typeof value === "string" && PROVIDER_IDENTIFIERS.has(value as ProviderIdentifier);

/**
 * Whether a value carries the fields the client provider renderer needs to mount
 * a payment form. Used to decide whether an SSR first paint can reuse the cookie
 * or has to fall back to a fresh `pick` call.
 *
 * Deliberately shape-only: it does not check `providerIdentifier` against
 * {@link isProviderIdentifier}. A pick creates a transaction, so rejecting a
 * cookie the backend just wrote (say a PSP id this frontend doesn't know yet)
 * would re-pick on every page load. A form that fails to render is recoverable;
 * a pick loop is not.
 */
export const isRenderableProviderCookie = (
  value: ProviderCookieValue | undefined
): value is ProviderCookieValue =>
  Boolean(
    value &&
    typeof value.orderId === "string" &&
    value.orderId.length > 0 &&
    typeof value.apiKey === "string" &&
    typeof value.providerIdentifier === "string"
  );
